import { useState } from 'react';
import { seedAPI } from '../../lib/api';
import { Zap, Bell, Mail, BarChart3, Trash2, CalendarDays, AlertTriangle } from 'lucide-react';

const TRIGGERS = [
  {
    id: 'alerts',
    label: 'Generare Alerte',
    description: 'Rulează job-ul de generare alerte (normalmente pornit la 20:00). Creează alerte pentru abonamente care expiră curând.',
    icon: Bell,
    color: 'yellow',
    fn: () => seedAPI.triggerAlerts(),
  },
  {
    id: 'feedback',
    label: 'Emailuri Feedback',
    description: 'Trimite emailuri de feedback pentru: (1) primul abonament cumpărat acum 30 de zile, (2) abonamente cu status=completed fără email trimis.',
    icon: Mail,
    color: 'blue',
    fn: () => seedAPI.triggerFeedback(),
  },
  {
    id: 'weekly-digest',
    label: 'Digest Săptămânal',
    description: 'Trimite digest-ul săptămânal pe email adminului (normalmente sâmbătă seara). Conține statistici și activitate recentă.',
    icon: CalendarDays,
    color: 'purple',
    fn: () => seedAPI.triggerWeeklyDigest(),
  },
  {
    id: 'monthly-report',
    label: 'Raport Lunar',
    description: 'Generează și trimite raportul Excel pe email pentru luna curentă (1 → azi). Normalmente rulează în prima zi a lunii.',
    icon: BarChart3,
    color: 'green',
    fn: () => seedAPI.triggerMonthlyReport(),
  },
  {
    id: 'cleanup',
    label: 'Cleanup Utilizatori Inactivi',
    description: 'Marchează utilizatorii inactivi pentru ștergere (automat o dată pe an, pe 1 ianuarie la 03:00). Utilizatorii fără abonament activ > 180 de zile (6 luni) sunt marcați.',
    icon: Trash2,
    color: 'red',
    fn: () => seedAPI.triggerCleanup(),
  },
];

const colorMap = {
  yellow: { icon: 'text-yellow-400', bg: 'bg-yellow-500/10', stripe: 'bg-yellow-400', btn: 'bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 border border-yellow-500/20' },
  blue:   { icon: 'text-blue-400',   bg: 'bg-blue-500/10',   stripe: 'bg-blue-400',   btn: 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20' },
  purple: { icon: 'text-purple-400', bg: 'bg-purple-500/10', stripe: 'bg-purple-400', btn: 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20' },
  green:  { icon: 'text-[#6db025]',  bg: 'bg-[#6db025]/10',  stripe: 'bg-[#6db025]',  btn: 'bg-[#6db025]/10 hover:bg-[#6db025]/20 text-[#6db025] border border-[#6db025]/20' },
  red:    { icon: 'text-red-400',    bg: 'bg-red-500/10',    stripe: 'bg-red-400',    btn: 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20' },
};

const TriggerCard = ({ trigger }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const colors = colorMap[trigger.color];
  const Icon = trigger.icon;

  const run = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await trigger.fn();
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Eroare necunoscută');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden flex flex-col">
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${colors.stripe}`} />
      <div className="p-6 pl-7 flex flex-col gap-4 flex-1">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 flex-shrink-0 flex items-center justify-center rounded ${colors.bg}`}>
            <Icon size={22} className={colors.icon} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold uppercase tracking-widest text-sm">{trigger.label}</h3>
            <p className="text-white/40 text-xs mt-1 leading-relaxed">{trigger.description}</p>
          </div>
        </div>

        <button
          onClick={run}
          disabled={loading}
          className={`flex items-center justify-center gap-2 px-4 py-3 text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50 rounded ${colors.btn}`}
        >
          {loading ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Se rulează...
            </>
          ) : (
            <>
              <Zap size={13} />
              Declanșează Acum
            </>
          )}
        </button>

        {result && (
          <div className="bg-black/40 border border-white/5 p-3 rounded-sm">
            <p className="text-white/30 text-[9px] font-black uppercase tracking-widest mb-2">Răspuns</p>
            <pre className="text-[#6db025] text-xs font-mono whitespace-pre-wrap break-all">
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}

        {error && (
          <div className="bg-red-500/5 border border-red-500/20 p-3 rounded-sm">
            <p className="text-red-400 text-xs font-mono">{error}</p>
            {error.includes('403') || String(error).includes('ALLOW_SEED') ? (
              <p className="text-white/30 text-[10px] mt-1">Adaugă <code className="text-yellow-400">ALLOW_SEED=true</code> în <code className="text-yellow-400">backend/.env</code> și repornește serverul.</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

const DevToolsPage = () => {
  return (
    <div className="space-y-8">
      {/* Header warning */}
      <div className="bg-yellow-500/5 border border-yellow-500/30 p-5 flex items-start gap-4">
        <AlertTriangle size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-yellow-400 font-black uppercase tracking-widest text-sm">Pagină de dezvoltare - Șterge înainte de lansare</p>
          <p className="text-white/40 text-xs mt-1">
            Aceste butoane declanșează manual job-urile programate (cron). Necesită <code className="text-yellow-300">ALLOW_SEED=true</code> în <code className="text-yellow-300">backend/.env</code>.
            Accesează această pagină la <code className="text-white/60">/admin/dev-tools</code>.
          </p>
        </div>
      </div>

      {/* Page title */}
      <div>
        <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Dev Tools</h1>
        <p className="text-white/40 text-sm mt-1">Declanșare manuală job-uri programate</p>
      </div>

      {/* Trigger cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {TRIGGERS.map(t => <TriggerCard key={t.id} trigger={t} />)}
      </div>
    </div>
  );
};

export default DevToolsPage;
